import {
  Button,
  HStack,
  PillToggleGroup,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Box } from '@chakra-ui/react';
import React from 'react';
import { Select } from '../Form/Select';
import {
  USER_TYPES,
  CARE_MODEL_OPTIONS,
  RISK_STRATIFICATION_OPTIONS,
  MEETINGS_FILTER_OPTIONS,
  MeetingsPresetOption,
  MeetingConfirmationPresetOption,
  ENROLLMENT_STATUS_OPTIONS,
  ONBOARDING_STATUS_OPTIONS,
  INITIAL_VISIT_STATUS_OPTIONS,
} from './constants';
import useCareTeamMember from './hooks/useCareTeamMember';
import { useDashboardCoach } from './hooks/useDashboardCoach';

const SIDEBAR_WIDTH = 296;

type SelectOption = { label: string; value: string } | null;

type Props = {
  selectedMeetings: string | null;
  selectedMeetingNumDays: number | undefined;
  onCareModelChange: (opt: SelectOption) => void;
  onRiskStratificationChange: (opt: SelectOption) => void;
  onCoachChange: (opt: SelectOption) => void;
  onMeetingsFilterChange: (meetings?: string, meetingNumDays?: number) => void;
  onUserTypeChange: (opt: SelectOption) => void;
  onProviderChange: (opt: SelectOption) => void;
  onMeetingConfirmedChange: (confirmed?: boolean) => void;
  onEnrollmentStatusChange: (id?: string) => void;
  onOnboardingStatusChange: (id?: string) => void;
  onInitialVisitStatusChange: (id?: string) => void;
  clearAllFilters: () => void;
  selectedCareModel: string | null;
  selectedRiskStratification: string | null;
  selectedCoach: string | null;
  selectedUserType: string | null;
  selectedProvider: string | null;
  selectedMeetingConfirmed: boolean | null;
  selectedEnrollmentStatus: string | null;
  selectedOnboardingStatus: string | null;
  selectedInitialVisitStatus: string | null;
};

const FilterSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <VStack gap={3} align="stretch">
    <Text
      my={0}
      fontSize="sm"
      fontWeight="semibold"
      color="secondary.500"
      letterSpacing="0.01em"
    >
      {title}
    </Text>
    <VStack gap={3} align="stretch">
      {children}
    </VStack>
  </VStack>
);

const FilterField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <VStack gap={1} align="stretch">
    <Text my={0} fontSize="xs" color="muted.500">
      {label}
    </Text>
    {children}
  </VStack>
);

const PillFilterGroup = ({
  title,
  options,
  selectedId,
  onSelectId,
  isDisabled,
}: {
  title: string;
  options: readonly { id: string; label: string }[];
  selectedId: string | undefined;
  onSelectId: (id: string | undefined) => void;
  isDisabled?: boolean;
}) => (
  <VStack gap={2} align="stretch">
    <Text my={0} fontSize="xs" color="muted.500">
      {title}
    </Text>
    <Box
      opacity={isDisabled ? 0.4 : 1}
      pointerEvents={isDisabled ? 'none' : 'auto'}
    >
      <PillToggleGroup
        multiple={false}
        options={options.map(({ id, label }) => ({ value: id, label }))}
        selected={selectedId ? [selectedId] : []}
        onChange={([next]) => onSelectId(next)}
      />
    </Box>
  </VStack>
);

const SidebarSelect = ({
  placeholder,
  options,
  value,
  onChange,
}: {
  placeholder: string;
  options: { label: string; value: string }[];
  value: SelectOption;
  onChange: (opt: SelectOption) => void;
}) => (
  <Select
    height={32}
    isClearable
    isSearchable={false}
    maxWidth={264}
    menuPlacement="auto"
    placeholder={placeholder}
    options={options}
    // null (not undefined) so react-select clears after Clear all
    value={value}
    onChange={onChange}
  />
);

export const FilterSidebar = ({
  selectedMeetings,
  selectedMeetingNumDays,
  onCareModelChange,
  onRiskStratificationChange,
  onCoachChange,
  onMeetingsFilterChange,
  onUserTypeChange,
  onProviderChange,
  onMeetingConfirmedChange,
  onEnrollmentStatusChange,
  onOnboardingStatusChange,
  onInitialVisitStatusChange,
  clearAllFilters,
  selectedCareModel,
  selectedRiskStratification,
  selectedCoach,
  selectedUserType,
  selectedProvider,
  selectedMeetingConfirmed,
  selectedEnrollmentStatus,
  selectedOnboardingStatus,
  selectedInitialVisitStatus,
}: Props) => {
  const { providerOptions } = useCareTeamMember();
  const { coachOptions } = useDashboardCoach();

  const selectedCareModelOpt =
    CARE_MODEL_OPTIONS.find((opt) => opt.value === selectedCareModel) ?? null;
  const selectedRiskStratificationOpt =
    RISK_STRATIFICATION_OPTIONS.find(
      (opt) => opt.value === selectedRiskStratification
    ) ?? null;
  const selectedCoachOpt =
    coachOptions?.find((opt) => opt.value === selectedCoach) ?? null;
  const selectedUserTypeOpt = selectedUserType
    ? { label: selectedUserType, value: selectedUserType }
    : null;
  const selectedProviderOpt =
    providerOptions?.find((opt) => opt.value === selectedProvider) ?? null;

  const isMeetingsPreset = (
    opt: typeof MEETINGS_FILTER_OPTIONS[number]
  ): opt is MeetingsPresetOption => opt.group !== 'Confirmations';
  const isConfirmationPreset = (
    opt: typeof MEETINGS_FILTER_OPTIONS[number]
  ): opt is MeetingConfirmationPresetOption => opt.group === 'Confirmations';

  const meetingsPresetOptions =
    MEETINGS_FILTER_OPTIONS.filter(isMeetingsPreset);
  const confirmationOptions =
    MEETINGS_FILTER_OPTIONS.filter(isConfirmationPreset);

  // At most one preset can be active at a time - PillToggleGroup owns the
  // per-pill selection/clear behavior (multiple={false}), so we only need to
  // translate the currently active meetings/meetingNumDays into an option id
  // and back.
  const selectedMeetingsOptionId = meetingsPresetOptions.find(
    (opt) =>
      opt.meetings === selectedMeetings &&
      (opt.meetingNumDays ?? undefined) ===
        (selectedMeetingNumDays ?? undefined)
  )?.id;

  const onSelectMeetingsOptionId = (id: string | undefined) => {
    const option = meetingsPresetOptions.find((opt) => opt.id === id);
    onMeetingsFilterChange(option?.meetings, option?.meetingNumDays);
  };

  const upcomingMeetingsOptions = meetingsPresetOptions.filter(
    (opt) => opt.group === 'Upcoming'
  );
  const pastMeetingsOptions = meetingsPresetOptions.filter(
    (opt) => opt.group === 'Past'
  );

  const selectedConfirmationOptionId = confirmationOptions.find(
    (opt) => opt.confirmed === selectedMeetingConfirmed
  )?.id;

  const onSelectConfirmationOptionId = (id: string | undefined) => {
    const option = confirmationOptions.find((opt) => opt.id === id);
    onMeetingConfirmedChange(option?.confirmed);
  };

  return (
    <Box
      as="aside"
      w={`${SIDEBAR_WIDTH}px`}
      flexShrink={0}
      h="100%"
      display="flex"
      flexDirection="column"
      bg="feather"
    >
      <Box flex={1} minH={0} overflowY="auto" px={4} pt={4} pb={4}>
        <HStack justifyContent="space-between" alignItems="center" mb={4}>
          <Text
            my={0}
            fontSize="sm"
            fontWeight="semibold"
            color="secondary.500"
          >
            Filters
          </Text>

          <Button
            variant="link"
            size="xs"
            onClick={clearAllFilters}
            aria-label="Clear all filters"
            _hover={{ bg: 'transparent', color: 'secondary.600' }}
            _active={{ bg: 'transparent' }}
          >
            Clear all
          </Button>
        </HStack>

        <VStack align="stretch" gap={6}>
          <FilterSection title="Care Team">
            <FilterField label="Providers">
              <SidebarSelect
                placeholder="Select a provider"
                onChange={onProviderChange}
                options={providerOptions ?? []}
                value={selectedProviderOpt}
              />
            </FilterField>

            <FilterField label="Coach">
              <SidebarSelect
                placeholder="Select a coach"
                onChange={onCoachChange}
                options={coachOptions ?? []}
                value={selectedCoachOpt}
              />
            </FilterField>
          </FilterSection>

          <FilterSection title="Appointments">
            <PillFilterGroup
              title="Upcoming"
              options={upcomingMeetingsOptions}
              selectedId={selectedMeetingsOptionId}
              onSelectId={onSelectMeetingsOptionId}
            />
            <PillFilterGroup
              title="Past"
              options={pastMeetingsOptions}
              selectedId={selectedMeetingsOptionId}
              onSelectId={onSelectMeetingsOptionId}
            />
            <PillFilterGroup
              title="Confirmations"
              options={confirmationOptions}
              selectedId={selectedConfirmationOptionId}
              onSelectId={onSelectConfirmationOptionId}
              isDisabled={
                !selectedMeetingsOptionId ||
                selectedMeetingsOptionId === 'noneScheduled'
              }
            />
          </FilterSection>

          <FilterSection title="Care Coordination">
            <PillFilterGroup
              title="Onboarding Status"
              options={ONBOARDING_STATUS_OPTIONS}
              selectedId={selectedOnboardingStatus ?? undefined}
              onSelectId={onOnboardingStatusChange}
            />
            <PillFilterGroup
              title="Initial Visit Status"
              options={INITIAL_VISIT_STATUS_OPTIONS}
              selectedId={selectedInitialVisitStatus ?? undefined}
              onSelectId={onInitialVisitStatusChange}
            />
            <PillFilterGroup
              title="Enrollment Status"
              options={ENROLLMENT_STATUS_OPTIONS}
              selectedId={selectedEnrollmentStatus ?? undefined}
              onSelectId={onEnrollmentStatusChange}
            />
          </FilterSection>

          <FilterSection title="Member Status">
            <FilterField label="Care Model">
              <SidebarSelect
                placeholder="Select a care model"
                onChange={onCareModelChange}
                options={CARE_MODEL_OPTIONS}
                value={selectedCareModelOpt}
              />
            </FilterField>
            <FilterField label="Risk Stratification">
              <SidebarSelect
                placeholder="Select a risk stratification"
                onChange={onRiskStratificationChange}
                options={RISK_STRATIFICATION_OPTIONS}
                value={selectedRiskStratificationOpt}
              />
            </FilterField>
          </FilterSection>

          <FilterSection title="Member Attributes">
            <FilterField label="User Type">
              <SidebarSelect
                placeholder="Select user type"
                onChange={onUserTypeChange}
                options={Object.values(USER_TYPES).map((uType) => ({
                  label: uType,
                  value: uType,
                }))}
                value={selectedUserTypeOpt}
              />
            </FilterField>
          </FilterSection>
        </VStack>
      </Box>
    </Box>
  );
};
